import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import api, { fmtDateTime } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import DataManagement from "@/components/DataManagement";
import { useAuth } from "@/context/AuthContext";

/* Module-level audit: central audit entries filtered by a search term, CSV export scoped. */
function ModuleAudit({ moduleName }) {
  const [logs, setLogs] = useState([]);
  const [q, setQ] = useState("");
  const load = useCallback(() => {
    const p = new URLSearchParams(); if (q) p.set("q", q);
    api.get(`/admin/audit-logs?${p.toString()}`).then(({ data }) => setLogs(data)).catch(() => {});
  }, [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const exportCsv = async () => {
    const p = new URLSearchParams(); if (q) p.set("q", q);
    try { const res = await api.get(`/admin/audit-logs/export?${p.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement("a");
      a.href = url; a.download = `audit-${moduleName}.csv`; a.click(); URL.revokeObjectURL(url);
    } catch (e) {}
  };
  return (
    <div>
      <div className="flex gap-3 mb-4">
        <div className="relative flex-1 max-w-sm"><Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search user, record…" className="pl-9" data-testid="module-audit-search" /></div>
        <Button variant="outline" onClick={exportCsv} data-testid="module-audit-export"><Icons.Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Timestamp</TableHead><TableHead>User</TableHead><TableHead>Action</TableHead><TableHead>Module</TableHead><TableHead className="hidden lg:table-cell">Record</TableHead></TableRow></TableHeader>
          <TableBody data-testid="module-audit-table">
            {logs.map((l) => (
              <TableRow key={l.id}>
                <TableCell className="text-xs text-slate-500 whitespace-nowrap">{fmtDateTime(l.timestamp)}</TableCell>
                <TableCell className="text-sm">{l.user_name}</TableCell>
                <TableCell><Badge className="text-[10px] bg-slate-100 hover:opacity-100">{l.action}</Badge></TableCell>
                <TableCell className="text-sm text-slate-600">{l.module}</TableCell>
                <TableCell className="hidden lg:table-cell font-mono text-[11px] text-slate-400 max-w-[160px] truncate">{l.record_id || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

/* Effective roles view: which master roles can access this module (read-only; managed centrally). */
function ModuleRoles({ perm }) {
  const [roles, setRoles] = useState([]);
  useEffect(() => { api.get("/admin/roles").then(({ data }) => setRoles(data)).catch(() => {}); }, []);
  const can = (r) => (r.permissions || []).includes("*") || (perm && (r.permissions || []).includes(perm));
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b text-sm text-slate-600">Effective module access (managed centrally in Master Settings → Roles). Required permission: <span className="font-mono">{perm || "—"}</span></div>
      <Table>
        <TableHeader><TableRow className="bg-slate-50"><TableHead>Master Role</TableHead><TableHead>Module Access</TableHead></TableRow></TableHeader>
        <TableBody data-testid="module-roles-table">
          {roles.map((r) => (
            <TableRow key={r.id || r.name}>
              <TableCell className="text-sm font-medium text-slate-800">{r.name}</TableCell>
              <TableCell>{can(r) ? <Badge className="bg-emerald-50 text-emerald-700 text-[10px]">Enabled</Badge> : <Badge className="bg-slate-100 text-slate-500 text-[10px]">No access</Badge>}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function ModuleSettings() {
  const { moduleId } = useParams();
  const { hasPerm } = useAuth();
  const [mod, setMod] = useState(null);
  const isAdmin = ["admin.users", "admin.roles", "admin.settings", "admin.audit"].some((p) => hasPerm(p));

  useEffect(() => {
    api.get("/modules").then(({ data }) => setMod(data.find((m) => m.module_id === moduleId) || { module_id: moduleId, name: moduleId }));
  }, [moduleId]);

  if (!mod) return <div className="p-10 text-slate-400">Loading…</div>;
  if (!isAdmin) {
    return (
      <div className="uemp-fade-in">
        <PageHeader title={`${mod.name} — Settings`} subtitle="Module configuration" />
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-slate-500" data-testid="module-settings-no-access">
          <Icons.Lock className="h-8 w-8 mx-auto mb-2 text-slate-300" /> You don't have permission to manage this module.
        </div>
      </div>
    );
  }

  return (
    <div className="uemp-fade-in">
      <PageHeader title={`${mod.name} — Settings`} subtitle="Module-scoped data management, audit, and effective access. Changes here never affect other modules." />
      <Tabs defaultValue="data">
        <TabsList className="mb-5">
          <TabsTrigger value="data" data-testid="msettings-tab-data"><Icons.DatabaseBackup className="h-4 w-4 mr-1.5" /> Data Management</TabsTrigger>
          <TabsTrigger value="audit" data-testid="msettings-tab-audit"><Icons.ScrollText className="h-4 w-4 mr-1.5" /> Audit Trail</TabsTrigger>
          <TabsTrigger value="roles" data-testid="msettings-tab-roles"><Icons.ShieldCheck className="h-4 w-4 mr-1.5" /> Roles</TabsTrigger>
        </TabsList>
        <TabsContent value="data"><DataManagement fixedScope={`module:${moduleId}`} allowPurge={false} /></TabsContent>
        <TabsContent value="audit"><ModuleAudit moduleName={mod.name} /></TabsContent>
        <TabsContent value="roles"><ModuleRoles perm={mod.required_permission} /></TabsContent>
      </Tabs>
    </div>
  );
}
